"use client";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
import { Heading2 } from "../typography";
import { ScrollItem } from "../nsw-water-compliance/scroll-item";
import { TrendChart } from "../shared/animated-icons/trend-chart";
import { AustraliaDots, Connectivity, LineChartUp, Target } from "../shared";
import { TheResults } from "./the-results";
import Image from "next/image";

export const HowItWorks = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate scroll distance - reusable function
  const calculateScrollDistance = () => {
    const content = contentRef.current;
    const scrollItems = scrollItemsRef.current.filter(Boolean);

    if (!content || !scrollItems.length) return 0;

    const gap = parseFloat(getComputedStyle(content).gap) || 0;
    const totalWidth =
      scrollItems.length * (scrollItems[0]?.offsetWidth ?? 0) +
      gap * (scrollItems.length - 1);
    const containerWidth = containerRef.current?.offsetWidth || 0;

    return totalWidth - containerWidth;
  };

  useSectionNavTheme({
    trigger: "#planr-how-it-works",
    isDark: false,
    // markers: true,
    end: () => {
      const scrollDistance = calculateScrollDistance();
      return `+=${scrollDistance + window.innerHeight + 160}`; // Added extra offset for better timing
    },
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const section = sectionRef.current;
      const content = contentRef.current;
      const container = containerRef.current;
      const scrollItems = scrollItemsRef.current.filter(Boolean);

      if (!section || !content || !scrollItems.length) return;

      // Initial title animation
      gsap.from(".works-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Get scroll distance using the shared calculation function
      const scrollDistance = calculateScrollDistance();

      // Create horizontal scroll timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${scrollDistance}`,
          scrub: 1,
          pin: container,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          // markers: true,
          onUpdate: (self) => {
            // Animate progress bars based on scroll progress
            const progress = self.progress;

            // Update progress bars for each scroll item
            scrollItems.forEach((item, index) => {
              const progressBar = item?.querySelector(".progress-bar");
              if (progressBar) {
                const itemProgress = Math.max(
                  0,
                  Math.min(1, progress * scrollItems.length - index)
                );
                gsap.set(progressBar, {
                  clipPath: `polygon(0% 0%, ${itemProgress * 100}% 0%, ${
                    itemProgress * 100
                  }% 100%, 0% 100%)`,
                });
              }
            });
          },
        },
      });

      // Animate horizontal movement
      tl.to(scrollItems, {
        x: -scrollDistance,
        duration: 1,
        ease: "none",
      });

      // Add stagger animation for scroll items appearing
      gsap.fromTo(
        scrollItems,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.2,
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "top 20%",
            scrub: false,
          },
        }
      );
    });
  });

  const workflowSteps = [
    {
      title: "Continental-Scale Real-Time Processing",
      description:
        "Terrak.io's distributed processing architecture handles massive environmental datasets across Australia, enabling instant analysis that would typically require supercomputer resources.",
      icon: (
        <AustraliaDots className="mx-auto w-24 xl:w-30 2xl:w-[150px] aspect-square" />
      ),
    },
    {
      title: "Contextual Environmental Analysis",
      description:
        "Advanced algorithms analyse landscape-scale environmental patterns rather than individual pixels, providing scientifically rigorous habitat connectivity and ecosystem impact assessments.",
      icon: (
        <TrendChart className="mx-auto w-24 xl:w-30 2xl:w-[150px] aspect-square" />
      ),
    },
    {
      title: "Seamless Integration",
      description:
        "The platform's API architecture integrates complex environmental calculations with user-friendly web interfaces, making sophisticated analysis accessible to landholders without technical expertise.",
      icon: (
        <Target className="mx-auto w-24 xl:w-30 2xl:w-[150px] aspect-square" />
      ),
    },
    {
      title: "Operational Scalability",
      description:
        "The infrastructure supports Australia's growing environmental markets, enabling thousands of simultaneous users to access real-time environmental analysis capabilities.",
      icon: (
        <LineChartUp className="mx-auto w-24 xl:w-30 2xl:w-[150px] aspect-square" />
      ),
    },
    {
      title: "Real-Time Habitat Connectivity",
      description:
        "The world's first platform enabling instant calculation of habitat connectivity changes across continental scales",
      icon: (
        <Connectivity className="mx-auto w-24 xl:w-30 2xl:w-[150px] aspect-square" />
      ),
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="planr-how-it-works"
      className="relative py-20 lg:py-24 bg-haizea-dark text-white overflow-hidden"
    >
      {/* Background Pattern - Abstract Blobs */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        {/* Wind Blob - Top Left */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-haizea-green/60 rounded-full blur-3xl animate-pulse" />

        {/* Water Blob - Top Right */}
        <div
          className="absolute top-40 -right-32 w-[500px] h-[500px] bg-haizea-blue/60 rounded-full blur-3xl"
          style={{ animationDelay: "2s" }}
        />

        {/* Earth Blob - Middle Left */}
        <div
          className="absolute top-1/2 -left-40 w-[600px] h-[600px] bg-haizea-orange/60 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        />

        {/* Wind Blob - Bottom Right */}
        <div className="absolute -bottom-40 right-20 w-[550px] h-[550px] bg-haizea-green/60 rounded-full blur-3xl" />

        {/* Small accent blobs */}
        <div
          className="absolute top-1/4 right-1/4 w-64 h-64 bg-haizea-blue/50 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-haizea-green/60 rounded-full blur-3xl"
          style={{ animationDelay: "3s" }}
        />
      </div>
      <Image
        src="/images/chart.webp"
        alt="Background Satellite Image"
        height={150}
        width={230}
        className="absolute bottom-[50vw] lg:-left-[5%] xl:left-30 z-20 hidden xl:block drop-shadow-2xl/80 w-52 xl:w-64 h-auto"
        draggable={false}
      />
      <Image
        src="/images/map.webp"
        alt="Background Satellite Image"
        height={250}
        width={330}
        className="absolute bottom-[50vw] lg:-left-[10%] xl:left-0 z-10 hidden md:block drop-shadow-2xl/80 md:w-52 xl:w-64 h-auto"
        data-speed="0.6"
        draggable={false}
      />
      <Image
        src="/images/bird2.webp"
        alt="Background Bird"
        height={250}
        width={330}
        className="absolute bottom-96 -right-[5%] xl:right-0 opacity-60 z-10 hidden md:block w-64 xl:w-96 h-auto drop-shadow-2xl/50 scale-x-[-1]"
        draggable={false}
        data-speed="0.3"
      />

      {/* Horizontal Scroll Section */}
      <div ref={containerRef} className="container">
        <div className="text-center mb-16">
          <Heading2 className="lg:mb-4 works-title">How It Works</Heading2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
            The platform solved critical technical challenges that previously
            limited environmental assessment capabilities
          </p>
        </div>
        <div ref={contentRef} className="flex gap-8 overflow-visible">
          {workflowSteps.map((step, index) => (
            <ScrollItem
              key={index}
              ref={(el) => {
                scrollItemsRef.current[index] = el;
              }}
              title={step.title}
              description={step.description}
            >
              {step.icon}
            </ScrollItem>
          ))}
        </div>
      </div>
      <TheResults />
    </section>
  );
};
